import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton, SubmissionLoader, InfoNotice,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { AssetsTablePartial } from '@Components/Partial/Product/ContentId';
import { localAsset } from '@Helpers/asset';
import {
  initiateFetchAssetsRequest,
  clearContentIdAssetsRequest,
} from '@Actions';
import { StyledSVG } from '@Components/Partial/Product/StyledSharedComponents.jsx';


const STORAGE_KEY = 'youtubeAssetsPageState_';

const AssetsListPage = ({
  small,
  loading,
  submitLoading,
  onboardingDataLoading,
  location,
  fetchAssetsData,
  clearAssetsData,
  paymentPending = false,
  assetsData = null,
  onboardingData = null,
  featureConfig = null,
}) => {
  const [query, setQuery] = React.useState({});
  const [currentPage, setCurrentPage] = React.useState(0);
  const [totalResults, setTotalResults] = React.useState(0);
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  const memoizedQueryString = React.useMemo(() => JSON.stringify(query), [query]);

  const { assetConfig } = featureConfig?.feature || {};
  const { contentIdAssetUploadEnabled } = featureConfig || {};

  React.useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY + memoizedQueryString);

    if (stored) {
      const { total, page: pageState } = JSON.parse(stored);
      setTotalResults(total);
      setCurrentPage(Math.max(0, pageState)); // Ensure page is never less than 0
    }
  }, [memoizedQueryString]);

  React.useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY + memoizedQueryString, JSON.stringify({
      total: totalResults,
      page: currentPage,
    }));
  }, [memoizedQueryString, totalResults, currentPage]);

  React.useEffect(() => {
    fetchAssetsData({
      ...query,
      skip: currentPage * 25,
      limit: 25,
    });

    return () => {
      clearAssetsData();
      sessionStorage.removeItem(STORAGE_KEY + memoizedQueryString);
    };
  }, [memoizedQueryString, totalResults, currentPage]);

  // Pagination handler
  const handlePage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handleCsvExport = () => {
    fetchAssetsData({
      ...query,
    }, true);
  };

  // Query change handler for filter/sort
  const handleQueryChange = (key, value) => {
    if (key === 'reset') {
      setQuery({});
      setTotalResults(0);
      setCurrentPage(0);
      sessionStorage.removeItem(STORAGE_KEY + JSON.stringify({}));
    } else {
      setQuery((prev) => {
        const next = { ...prev, [key]: value };
        // Remove empty values
        Object.keys(next).forEach((k) => {
          if (
            next[k] === ''
            || next[k] === null
            || typeof next[k] === 'undefined'
          ) {
            delete next[k];
          }
        });
        return next;
      });
      setTotalResults(0);
      setCurrentPage(0);
      sessionStorage.removeItem(STORAGE_KEY + JSON.stringify(query));
    }
  };

  const dashboardPath = (assetId) => paths.productAssetDetails.replace(':id', assetId);
  const isRedflagUploadCurrent = onboardingData?.youtubeCid?.uploader === 'redflag';
  const stableIsRedflagUploadRef = React.useRef(isRedflagUploadCurrent);
  if (!onboardingDataLoading) {
    stableIsRedflagUploadRef.current = isRedflagUploadCurrent;
  }
  const isRedflagUpload = stableIsRedflagUploadRef.current;
  const showRedflagUploadNotice = isRedflagUpload
    && (!assetsData || assetsData?.data?.length === 0);
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const renderInfoNotice = () => {
    if (showRedflagUploadNotice) {
      return (
        <InfoNotice header="Redflag is processing your assets">
          <Box gap="0.5rem">
            <Text color={page.primaryText} size="0.9rem">
              You have selected Redflag managed upload. Please allow up to 48 hours
              for your assets to be processed and appear here.
            </Text>
            <Text color={page.primaryText} size="0.9rem">
              You can track upload progress on the&nbsp;
              <a
                href={paths.productAssetUpload}
                style={{ color: page.altHighlight, textDecoration: 'underline' }}
              >
                upload requests
              </a>
              &nbsp;page. If you have any questions, please reach out to support at&nbsp;
              <a
                href="mailto:contact@redflagai.co"
                style={{ color: page.altHighlight, textDecoration: 'underline' }}
              >
                contact@redflagai.co
              </a>
            </Text>
          </Box>
        </InfoNotice>
      );
    }

    return (
      <InfoNotice
        header="Asset data update schedule"
        text="This page displays asset data from a daily refresh cycle. Updates may not be visible until the next sync."
      />
    );
  };

  const renderBannerChildren = () => {
    if (onboardingDataLoading) return null;

    if (isRedflagUpload) {
      return (
        <AppButton
          overrideHover
          href={paths.productAssetUpload}
          label="View uploads"
          level="tableControls"
          bgColor={isDarkMode ? button.primaryBg : page.background}
          color={isDarkMode ? button.primaryText : page.primaryText}
        />
      );
    }


    if (contentIdAssetUploadEnabled) {
      return (
        <AppButton
          overrideHover
          href={paths.productAssetUpload}
          label="Upload assets"
          level="tableControls"
          bgColor={isDarkMode ? button.primaryBg : page.background}
          color={isDarkMode ? button.primaryText : page.primaryText}
          icon={(
            <Box pad={{ top: '0.2rem' }}>
              <StyledSVG
                src={localAsset('images/upload-file-icon.svg')}
                width={small ? '1.5rem' : '1.25rem'}
                height={small ? '1.25rem' : '1rem'}
                title="Upload assets"
                $fillColor={isDarkMode ? button.primaryText : page.primaryText}
              />
            </Box>
        )}
        />
      );
    }

    return null;
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manage assets',
        subTitle: 'Content ID',
        bannerChildrenPosition: 'bottom',
        bannerChildren: (
          <Box width="fit-content">
            {renderBannerChildren()}
          </Box>
        ),
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Exporting CSV..."
          contentWidth="20rem"
        />
      )}
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
      >
        {renderInfoNotice()}
        <AssetsTablePartial
          small={small}
          assetsData={assetsData}
          companyAssetConfig={assetConfig}
          dashboardPath={dashboardPath}
          onQueryChange={handleQueryChange}
          onClickExport={handleCsvExport}
          handlePage={handlePage}
          currentPage={currentPage}
          query={query}
          loading={loading || onboardingDataLoading}
          paymentPending={paymentPending}
          isRedflagUpload={isRedflagUpload}
          contentIdAssetUploadEnabled={contentIdAssetUploadEnabled}
        />
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    assetsData: state.contentIdAssets.assets,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    onboardingData: state.payment.customerOnboardingData,
    onboardingDataLoading: state.payment.customerOnboardingDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchAssetsData: initiateFetchAssetsRequest,
    clearAssetsData: clearContentIdAssetsRequest,
  }, dispatch);
}

AssetsListPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  onboardingDataLoading: PropTypes.bool.isRequired,
  fetchAssetsData: PropTypes.func.isRequired,
  clearAssetsData: PropTypes.func.isRequired,
  assetsData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      timeCreated: PropTypes.string.isRequired,
      metadata: PropTypes.objectOf(PropTypes.any),
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      customId: PropTypes.string.isRequired,
      totalClaims: PropTypes.number.isRequired,
      claimStats: PropTypes.arrayOf(PropTypes.shape({
        count: PropTypes.number.isRequired,
        status: PropTypes.string.isRequired,
      })).isRequired,
    })).isRequired,
    pagination: PropTypes.shape({
      totalResults: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }).isRequired,
  }),
  paymentPending: PropTypes.bool.isRequired,
  onboardingData: PropTypes.shape({
    onboardingId: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    paymentPending: PropTypes.bool.isRequired,
    paymentLink: PropTypes.string,
    youtubeCid: PropTypes.shape({
      uploader: PropTypes.string,
      plan: PropTypes.string,
    }),
  }),
  featureConfig: PropTypes.shape({
    feature: PropTypes.shape({
      assetConfig: PropTypes.arrayOf(PropTypes.any),
    }),
    contentIdAssetUploadEnabled: PropTypes.bool,
    hasAllowlistedChannels: PropTypes.bool,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(AssetsListPage));
